import React from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { ago } from '../lib/format.js';
import { Card, useAsync } from './ui.jsx';
import { useSession } from '../App.jsx';
import { ROLES, can } from '../lib/roles.js';

// `roles` hides a link to a screen the reader's role cannot open.
const WHERE = {
  agent_offline: { to: '/sync', label: 'Open the sync console', roles: ROLES.ADMIN_ACCOUNTS },
  sync_failed: { to: '/sync?jobs=failed', label: 'See the failed jobs', roles: ROLES.ADMIN_ACCOUNTS },
  invoice_unposted: { to: '/invoices?sync_status=pending,queued,failed', label: 'See the invoices' },
  spec_blocked: { to: '/batches?spec_status=pending', label: 'Record the properties' },
  batch_expiring: { to: '/batches?expiring=30', label: 'See those batches' },
  credit_limit: { to: '/delivery-orders', label: 'Review the orders' },
  rate_below_floor: { to: '/approvals', label: 'Review the approvals', roles: ROLES.COMMERCIAL },
  collections: { to: '/receivables?only=overdue', label: 'See who to chase', roles: ROLES.COMMERCIAL },
  not_posted: { to: '/sync', label: 'Confirm them in Tally sync', roles: ROLES.ADMIN_ACCOUNTS },
};

/**
 * What is quietly wrong. Shown at the top of the dashboard, because the failure
 * that matters most here is the one nobody is looking for: an agent that stopped
 * polling, invoices stuck outside Tally, stock that cannot be billed.
 */
export default function AlertPanel({ compact = false }) {
  const session = useSession();
  const alerts = useAsync(() => api.get('/api/alerts'), []);
  if (!alerts.data || alerts.data.alerts.length === 0) return null;

  const open = alerts.data.alerts.filter((a) => !a.resolved_at);
  if (open.length === 0) return null;
  const shown = compact ? open.slice(0, 3) : open;

  const acknowledge = async (id) => {
    await api.post(`/api/alerts/${id}/acknowledge`).catch(() => {});
    alerts.reload();
  };

  return (
    <Card title={`Needs attention (${open.length})`}
      note="Raised automatically, and cleared automatically once the condition is gone."
      className="alert-panel">
      <div className="stack" style={{ gap: 10 }}>
        {shown.map((a) => {
          const link = WHERE[a.kind];
          const where = link && (!link.roles || can(session, link.roles)) ? link : null;
          return (
            <div key={a.id} className={`attn ${a.severity}`}>
              <div className="attn-dot" aria-hidden="true" />
              <div style={{ flex: 1 }}>
                <div className="attn-title">{a.title}</div>
                <div className="sub">{a.detail}</div>
                <div className="sub">
                  first seen {ago(a.first_seen_at)}
                  {a.acknowledged_by_name ? ` · seen by ${a.acknowledged_by_name}` : ''}
                </div>
              </div>
              <div className="inline">
                {where && <Link className="btn small secondary" to={where.to}>{where.label}</Link>}
                {!a.acknowledged_at && (
                  <button className="btn small ghost" onClick={() => acknowledge(a.id)}>Mark seen</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {compact && open.length > shown.length && (
        <div className="card-note" style={{ marginTop: 10 }}>and {open.length - shown.length} more</div>
      )}
    </Card>
  );
}
